import type { WidgetSettings } from '@support/widget-contracts';

/**
 * What the host page told us about where the visitor is — captured with the
 * first message of a new conversation, so the agent does not open by asking
 * "which page, which stream".
 *
 * A module-level value rather than component state, for the reason the session
 * is one: `App.vue` receives the settings, the screen that files a ticket is
 * three components away, and threading two fields through every one of them
 * would put the host's context in props that exist only to be forwarded.
 */
export interface CapturedContext {
  pageUrl?: string;
  entries: { key: string; value: string }[];
}

/**
 * The API's own limits (`ticket-context.ts`), applied here first.
 *
 * The API trims rather than refuses — but only once the request has been
 * parsed, and the body parser refuses anything over its size limit before that.
 * A publisher who hands `setContext()` a large object would otherwise push the
 * whole message over it, and the visitor would be told the conversation could
 * not be started, on every attempt, for the sake of a hint.
 */
const MAX_ENTRIES = 20;
const MAX_KEY_LENGTH = 64;
const MAX_VALUE_LENGTH = 500;
const MAX_PAGE_URL_LENGTH = 2000;

let pageUrl: string | undefined;
let context: Record<string, unknown> | undefined;

export const setHostContext = (settings: Pick<WidgetSettings, 'pageUrl' | 'context'>): void => {
  pageUrl = settings.pageUrl;
  context = settings.context;
};

/**
 * Host context as the API takes it: flat string pairs.
 *
 * `setContext()` accepts any plain data, and a publisher will pass numbers,
 * booleans and the occasional nested object. Strings stay as they are;
 * everything else is serialised, so `{ stream: { id: 7 } }` reaches the agent
 * as `stream = {"id":7}` rather than as `[object Object]` or not at all. Empty
 * values are dropped and long ones cut here — the API would do both too, and
 * there is no reason to post what cannot be stored.
 */
export const captureContext = (): CapturedContext | undefined => {
  const entries = Object.entries(context ?? {})
    .map(([key, value]) => ({
      key: key.trim().slice(0, MAX_KEY_LENGTH),
      value: stringify(value).trim().slice(0, MAX_VALUE_LENGTH),
    }))
    .filter((entry) => entry.key !== '' && entry.value !== '')
    .slice(0, MAX_ENTRIES);

  // Dropped rather than cut: half a URL is a different URL.
  const page = pageUrl && pageUrl.length <= MAX_PAGE_URL_LENGTH ? pageUrl : undefined;

  if (!page && entries.length === 0) {
    return undefined;
  }

  return { ...(page ? { pageUrl: page } : {}), entries };
};

const stringify = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (value === null || value === undefined) {
    return '';
  }

  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return '';
  }
};
